import React, {
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'

import PropTypes from 'prop-types'

import Modal from 'components/Modal'

import ModalContent from 'components/ModalContent'

import { ModalContext } from './modalContext'


/**
 * Do not use this component near the root of the application (e.g. in the router).
 * This provider needs to be unmounted between pages so that the underlying
 * modal component can clean up after itself when a link to another page is followed,
 * otherwise, it's easy to get into a situation where the "overflow: hidden" on the
 * whole page will be orphaned, and the page will be unscrollable.
 *
 * @returns {Node}
 */
export default function ModalProvider({ children }) {
  const previousActiveElement = useRef(null)

  const [isOpen, setOpen] = useState(false)
  const [isDismissible, setDismissible] = useState(true)
  const [modalState, setModalState] = useState({
    ariaLabel: void 0,
    content: null,
  })

  const close = useCallback(() => {
    setOpen(false)

    previousActiveElement.current?.focus()
    previousActiveElement.current = null
  }, [])

  const open = useCallback(() => {
    previousActiveElement.current = document.activeElement

    setOpen(true)
  }, [])

  // Content registers a `handleClose` when dismissing has to do more than hide
  // the modal, and withdraws it (with `null`) once it no longer does. The
  // handler runs everywhere the user can dismiss — the close button, the
  // backdrop, and Escape — and the modal still closes itself afterwards, so
  // content can't leave it stuck open by forgetting to.
  const setHandleClose = useCallback((handleClose) => {
    setModalState((state) => ({
      ...state,
      handleClose,
    }))
  }, [])

  const setModalContent = useCallback((content, options = {}) => {
    setModalState({
      ariaLabel: options.ariaLabel,
      content,
    })
  }, [])

  const value = useMemo(() => ({
    close,
    open,
    setDismissible,
    setHandleClose,
    setModalContent,
  }), [close, open, setHandleClose, setModalContent])

  return (
    <ModalContext.Provider value={ value }>
      { children }

      {
        renderModal({
          close,
          isDismissible,
          isOpen,
          modalState,
        })
      }
    </ModalContext.Provider>
  )
}

ModalProvider.propTypes = {
  children: PropTypes.node,
}


function renderModal({ close, isDismissible, isOpen, modalState }) {
  // If we don't unmount the modal when it is closed, its internal
  // cleanup won't get triggered, which will leave the page
  // unscrollable. It will also leave the modal in the DOM and cause
  // problems if a page to which we're navigating does not have the store
  // support for the displayed modal.
  if ( !isOpen ) {
    return null
  }

  const handleClose = (() => {
    if ( !isDismissible ) {
      return
    }

    return () => {
      modalState.handleClose?.()

      close()
    }
  })()

  return (
    <Modal
      ariaLabel={ modalState.ariaLabel }
      handleClickOutside={ handleClose }
    >
      <ModalContent
        handleClose={ handleClose }
      >
        <Suspense fallback={ null }>
          { modalState.content }
        </Suspense>
      </ModalContent>
    </Modal>
  )
}
